"use client";

import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  CalendarDays,
  Users,
  Clock,
  Mail,
  Share2,
  Home,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SITE } from "@/lib/constants";

const formatINR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

interface BookingConfirmationProps {
  referenceNumber: string;
  checkIn: Date;
  checkOut: Date;
  guests: number;
  amountPaid: number;
  guestEmail: string;
}

export function BookingConfirmation({
  referenceNumber,
  checkIn,
  checkOut,
  guests,
  amountPaid,
  guestEmail,
}: BookingConfirmationProps) {
  const whatsappText = encodeURIComponent(
    `I just booked ${SITE.name}!\n` +
      `Ref: ${referenceNumber}\n` +
      `${format(checkIn, "MMM d")} – ${format(checkOut, "MMM d, yyyy")}\n` +
      `${guests} guests\n\n` +
      SITE.url
  );

  return (
    <div className="flex flex-col items-center text-center space-y-6">
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 15,
          delay: 0.1,
        }}
      >
        <div className="relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.3, 1] }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="absolute inset-0 rounded-full bg-primary/20 blur-xl"
            style={{ width: 96, height: 96 }}
          />
          <CheckCircle2 className="relative size-24 text-primary" strokeWidth={1.5} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-2"
      >
        <h2 className="font-playfair text-2xl font-bold text-foreground md:text-3xl">
          Booking Confirmed!
        </h2>
        <p className="text-muted-foreground">
          Your luxury getaway is reserved
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="rounded-xl border border-primary/30 bg-primary/5 px-6 py-3"
      >
        <p className="text-sm text-muted-foreground">Booking Reference</p>
        <p className="text-2xl font-bold tracking-wider text-primary">
          {referenceNumber}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="w-full max-w-sm rounded-xl border border-border bg-secondary/30 p-5 space-y-4"
      >
        <h3 className="font-playfair font-semibold text-foreground">
          Booking Summary
        </h3>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3">
            <CalendarDays className="size-4 text-primary shrink-0" />
            <span className="text-foreground">
              {format(checkIn, "EEE, MMM d")} – {format(checkOut, "EEE, MMM d, yyyy")}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Users className="size-4 text-primary shrink-0" />
            <span className="text-foreground">
              {guests} {guests === 1 ? "Guest" : "Guests"}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="size-4 shrink-0 text-center text-primary font-bold">₹</span>
            <span className="text-foreground">
              Amount Paid: {formatINR.format(amountPaid)}
            </span>
          </div>
        </div>

        <Separator className="bg-border/50" />

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-3">
            <Clock className="size-4 text-primary shrink-0" />
            <span className="text-foreground">Check-in: 2:00 PM</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="size-4 text-primary shrink-0" />
            <span className="text-foreground">Check-out: 11:00 AM</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.85 }}
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <Mail className="size-4 text-primary" />
        <span>
          Confirmation sent to <strong className="text-foreground">{guestEmail}</strong>
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="flex flex-col gap-3 w-full max-w-xs pt-2"
      >
        <Button
          asChild
          className="w-full bg-[#25D366] text-white hover:bg-[#25D366]/90 rounded-xl"
        >
          <a
            href={`https://wa.me/?text=${whatsappText}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Share2 className="size-4" />
            Share on WhatsApp
          </a>
        </Button>

        <Button
          asChild
          variant="outline"
          className="w-full border-border text-foreground hover:bg-secondary rounded-xl"
        >
          <Link href="/">
            <Home className="size-4" />
            Back to Home
          </Link>
        </Button>
      </motion.div>
    </div>
  );
}
